/**
 * Thin API layer. In VITE_USE_MOCKS mode we return the design's mock data so
 * the UI is browsable offline. In live mode we read directly from Supabase
 * (read paths) and POST to Express (write paths).
 */

import { getSupabase, USE_MOCKS } from './supabase';
import { readSowError } from './sow-validation';
import { draftMatchesScope, type EngagementScope } from '../../../src/reports/shared/engagement-scope';
import { GENERATIONS } from '../mocks/generations';
import { ROWS, aggregateMock, enrichMockRow } from '../mocks/rows';
import { SEND_BATCHES } from '../mocks/sends';
import {
  ENGAGEMENT_ALERTS,
  ENGAGEMENT_ASSESSMENTS,
  ENGAGEMENT_SNAPSHOT,
} from '../mocks/engagement-health';
import { OUTREACH_MOCK } from '../mocks/outreach';
import { OUTREACH_INBOX_MOCK } from '../mocks/outreach-inbox';
import { SOW_DOCUMENTS, SOW_OPPORTUNITIES, mockAppendSowDocument, mockSowPrefill } from '../mocks/sow';
import {
  mockChangeRole,
  mockDeactivate,
  mockGetTeam,
  mockInvite,
  mockResend,
  mockRestore,
  mockRevoke,
} from '../mocks/team';
import type { DashboardRole } from '../../../src/services/team/roles';
import {
  CHEMISTRY_CASES_MOCK,
  CANNED_AVAILABILITY_PARSE,
  CANNED_KICKOFF_PARSE,
} from '../mocks/chemistryChecks';
import type {
  AlertRow,
  AlertType,
  ChemistryCase,
  ChemistryCaseCreateInput,
  ChemistryRuntime,
  ChemistrySimulateRequest,
  ChemistrySimulateResponse,
  ChemistryTimelineEvent,
  DeliveringAffiliateRef,
  EngagementAssessment,
  EngagementDigestConfig,
  EngagementDigestRun,
  EngagementDigestRunDetail,
  EngagementHealthPayload,
  EngagementSnapshot,
  Generation,
  InboundReplyRow,
  ReplyLabelOverride,
  ReplyQueueStatus,
  ReplyReview,
  LedgerEntry,
  OutreachDeliveryMode,
  OutreachDraftStatus,
  OutreachRuntimeConfig,
  OutreachEngagement,
  OutreachPayload,
  OutreachWorkflow,
  RecordSendResult,
  ReportRow,
  SendBatch,
  SentSource,
  SentStatus,
  SowDocumentRow,
  SowFieldValues,
  SowOpportunityHit,
  SowPrefill,
  SowTemplateType,
  TeamInviteResult,
  TeamPayload,
} from '../types';

interface DbRow {
  id: string;
  generated_at: string;
  generated_by: string | null;
  range_months: number | null;
  date_range_start: string;
  date_range_end: string;
  row_count: number;
  status: 'running' | 'succeeded' | 'failed';
  error_message: string | null;
  storage_path: string | null;
  sessions_completed: number | null;
  total_hours_incurred: number | null;
  total_fee_budget: number | null;
  active_engagements: number | null;
}

interface DbReportRow {
  id: string;
  account_name: string | null;
  engagement_template: string | null;
  affiliate_full_name: string | null;
  coachee: string | null;
  status: string | null;
  sow_engagement_start_date: string | null;
  sow_engagement_end_date: string | null;
  total_hours_budget: number | null;
  total_hours_incurred: number | null;
  netsuite_opportunity_number: string | null;
  project_milestone_name: string | null;
  date_incurred: string | null;
  session_created_date: string | null;
  hours: number | null;
  affiliate_paid_hourly_rate: number | null;
  total_client_fee_budget: number | null;
  client_hourly_fee: number | null;
  netsuite_opportunity_id: string | null;
  netsuite_project_id: string | null;
  coaching_com_engagement_id: string | null;
  coaching_com_session_id: string | null;
  cogs: number | null;
  total_hours_over_budget: string | null;
  affiliate_rate_pct_of_client: number | null;
  coachee_last_name: string | null;
  coachee_last_name_derived: boolean | null;
  line_memo: string | null;
  reference_memo: string | null;
  reference_memo_length: number | null;
  truncated_name: string | null;
  vendor_name_netsuite: string | null;
  internal_id_project: string | null;
  hours_revenue: number | null;
  expiration_date: string | null;
  enriched_fields: string[] | null;
  sent_status: SentStatus | null;
  sent_at: string | null;
  sent_batch_id: string | null;
  sent_source: SentSource | null;
  sent_changed_fields: string[] | null;
}

export interface ReportSummary extends Generation {
  // KPI aggregates pulled from finance_reports for the latest-report KPI strip
  sessions_completed: number | null;
  total_hours_incurred: number | null;
  total_fee_budget: number | null;
  active_engagements: number | null;
  storage_path: string | null;
}

function mapStatus(s: 'running' | 'succeeded' | 'failed'): Generation['status'] {
  if (s === 'running') return 'Running';
  if (s === 'failed') return 'Failed';
  return 'Succeeded';
}

function mapDbRowToSummary(r: DbRow): ReportSummary {
  return {
    id: r.id,
    generatedAt: r.generated_at,
    rangeStart: r.date_range_start,
    rangeEnd: r.date_range_end,
    rows: r.row_count,
    user: r.generated_by ?? 'Cron · weekly',
    status: mapStatus(r.status),
    sessions_completed: r.sessions_completed,
    total_hours_incurred: r.total_hours_incurred,
    total_fee_budget: r.total_fee_budget,
    active_engagements: r.active_engagements,
    storage_path: r.storage_path,
  };
}

function mapDbReportRow(r: DbReportRow): ReportRow {
  return {
    id: r.id,
    account: r.account_name,
    template: r.engagement_template,
    affiliate: r.affiliate_full_name,
    coachee: r.coachee,
    status: r.status ?? 'Active',
    sowStart: r.sow_engagement_start_date,
    sowEnd: r.sow_engagement_end_date,
    hBudget: r.total_hours_budget,
    hIncurred: r.total_hours_incurred,
    oppNum: r.netsuite_opportunity_number,
    milestone: r.project_milestone_name,
    dateInc: r.date_incurred,
    created: r.session_created_date,
    hours: r.hours,
    rate: r.affiliate_paid_hourly_rate,
    feeBudget: r.total_client_fee_budget,
    clientFee: r.client_hourly_fee,
    oppId: r.netsuite_opportunity_id,
    projectId: r.netsuite_project_id,
    engagementId: r.coaching_com_engagement_id,
    sessionId: r.coaching_com_session_id,
    cogs: r.cogs,
    overBudget: r.total_hours_over_budget,
    ratePct: r.affiliate_rate_pct_of_client,
    coacheeLast: r.coachee_last_name,
    coacheeLastDerived: r.coachee_last_name_derived ?? false,
    lineMemo: r.line_memo,
    refMemo: r.reference_memo,
    refMemoLen: r.reference_memo_length,
    truncatedName: r.truncated_name,
    vendorName: r.vendor_name_netsuite,
    internalId: r.internal_id_project,
    hoursRevenue: r.hours_revenue,
    expiration: r.expiration_date,
    enrichedFields: r.enriched_fields ?? [],
    sentStatus: r.sent_status ?? 'unsent',
    sentAt: r.sent_at,
    sentBatchId: r.sent_batch_id,
    sentSource: r.sent_source,
    sentChangedFields: r.sent_changed_fields ?? [],
  };
}

export async function listReports(): Promise<ReportSummary[]> {
  if (USE_MOCKS) {
    const agg = aggregateMock(ROWS);
    return GENERATIONS.map((g) => ({
      ...g,
      sessions_completed: agg.sessions,
      total_hours_incurred: agg.hours,
      total_fee_budget: agg.fee,
      active_engagements: agg.engagements,
      storage_path: null,
    }));
  }
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('finance_reports')
    .select('*')
    .order('generated_at', { ascending: false })
    .limit(50);
  if (error) throw new Error(`listReports failed: ${error.message}`);
  return (data as DbRow[]).map(mapDbRowToSummary);
}

export async function getReport(id: string): Promise<ReportSummary | null> {
  if (USE_MOCKS) {
    const list = await listReports();
    return list.find((r) => r.id === id) ?? list[0] ?? null;
  }
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('finance_reports')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(`getReport failed: ${error.message}`);
  return data ? mapDbRowToSummary(data as DbRow) : null;
}

// PostgREST caps each response at the project's `max-rows` setting (default
// 1000), so `.limit(50_000)` alone silently truncates. Paginate via `.range()`
// until we get a short page back.
const PAGE_SIZE = 1_000;

async function fetchAllPages<T>(
  build: (from: number, to: number) => PromiseLike<{ data: unknown; error: { message: string } | null }>,
  label: string,
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await build(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(`${label} failed: ${error.message}`);
    const page = (data ?? []) as T[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) break;
  }
  return rows;
}

export async function listReportRows(reportId: string): Promise<ReportRow[]> {
  if (USE_MOCKS) {
    void reportId; // mocks don't vary by report ID
    return ROWS.map(enrichMockRow);
  }
  const supabase = getSupabase();
  const rows = await fetchAllPages<DbReportRow>(
    (from, to) => supabase
      .from('finance_report_rows')
      .select('*')
      .eq('report_id', reportId)
      .order('date_incurred', { ascending: false })
      .range(from, to),
    'listReportRows',
  );
  return rows.map(mapDbReportRow);
}

async function authHeader(): Promise<Record<string, string>> {
  if (USE_MOCKS) return {};
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type GenerateRequest = { rangeMonths: number } | { dateFrom: string; dateTo: string };

export async function generateReport(req: GenerateRequest): Promise<{ reportId: string; rowCount: number }> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 1200));
    return { reportId: `mock-${Date.now()}`, rowCount: ROWS.length };
  }
  const res = await fetch('/api/dashboard/reports/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(req),
  });
  if (!res.ok) throw new Error(`generateReport failed: ${res.status}`);
  const json = await res.json();
  return { reportId: json.reportId, rowCount: json.rowCount };
}

/**
 * Triggers a download of the cached .xlsx. We can't expose the API URL as a
 * plain <a href> because /api/dashboard/* is gated by requireSupabaseAuth and
 * browser navigation doesn't attach the Authorization header — the request
 * would 401 before the redirect could fire. Instead, fetch the signed URL
 * with the token, then navigate the browser to that direct Supabase URL.
 */
export async function downloadReport(id: string): Promise<void> {
  if (USE_MOCKS) {
    // Mocks mode has no Storage; pretend it succeeded.
    return;
  }
  const res = await fetch(`/api/dashboard/reports/${id}/download`, {
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`downloadReport failed: ${res.status}`);
  const { url } = (await res.json()) as { url: string };
  if (!url) throw new Error('downloadReport: response missing url');
  // Use window.open in a new tab so the dashboard stays mounted; signed
  // URLs from Supabase Storage trigger a direct download via Content-Disposition.
  window.open(url, '_blank', 'noopener,noreferrer');
}

export interface FilterPayload {
  accounts?: string[];
  affiliates?: string[];
  statuses?: string[];
  coachee?: string;
  oppNumber?: string;
  dateFrom?: string;
  dateTo?: string;
  sentStatuses?: SentStatus[];
}

export async function downloadFiltered(id: string, filters: FilterPayload): Promise<void> {
  if (USE_MOCKS) {
    // No-op in mocks — pretend it downloads
    return;
  }
  const res = await fetch(`/api/dashboard/reports/${id}/download-filtered`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(filters),
  });
  if (!res.ok) throw new Error(`downloadFiltered failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Coaching-Finance-Report-filtered-${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

// ============================================================
// Sent to finance
// ============================================================

interface DbSendBatch {
  id: string;
  sent_at: string;
  recorded_by_email: string | null;
  source: SentSource;
  original_filename: string | null;
  storage_path: string | null;
  session_count: number;
  new_count: number | null;
  resent_count: number | null;
  unmatched_count: number | null;
  note: string | null;
}

function mapDbSendBatch(b: DbSendBatch): SendBatch {
  return {
    id: b.id,
    sentAt: b.sent_at,
    recordedByEmail: b.recorded_by_email,
    source: b.source,
    originalFilename: b.original_filename,
    storagePath: b.storage_path,
    sessionCount: b.session_count,
    newCount: b.new_count,
    resentCount: b.resent_count,
    unmatchedCount: b.unmatched_count,
    note: b.note,
  };
}

export async function listSendBatches(): Promise<SendBatch[]> {
  if (USE_MOCKS) return SEND_BATCHES;
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('finance_sent_batches')
    .select('*')
    .order('sent_at', { ascending: false })
    .limit(100);
  if (error) throw new Error(`listSendBatches failed: ${error.message}`);
  return (data as DbSendBatch[]).map(mapDbSendBatch);
}

const MOCK_SEND_RESULT: RecordSendResult = {
  batchId: 'mock-batch',
  counts: { total: 212, newSessions: 198, previouslySent: 9, notFoundInReports: 5 },
  rowsRestamped: 230,
  skippedRowNumbers: [48],
  duplicateSessionIds: [],
  sheetName: 'Finance Output',
};

async function parseSendResponse(res: Response, label: string): Promise<RecordSendResult> {
  if (!res.ok) {
    // Surface the backend's structured message (BAD_FILE / PARSE_FAILED / …) when present.
    const detail = await res
      .json()
      .then((j) => (j as { error?: { message?: string } }).error?.message)
      .catch(() => undefined);
    throw new Error(detail ? `${label} failed: ${detail}` : `${label} failed: ${res.status}`);
  }
  return (await res.json()) as RecordSendResult;
}

/** Records "this exact file went to Finance" by POSTing the raw .xlsx bytes. */
export async function recordSendUpload(file: File, note?: string): Promise<RecordSendResult> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 900));
    return MOCK_SEND_RESULT;
  }
  const params = new URLSearchParams({ filename: file.name });
  if (note) params.set('note', note);
  const res = await fetch(`/api/dashboard/sent/upload?${params.toString()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ...(await authHeader()),
    },
    body: file,
  });
  return parseSendResponse(res, 'recordSendUpload');
}

/** Records a pasted ID list (e.g. the ones Ambuj confirmed were loaded). */
export async function recordSendPaste(sessionIds: string[], note?: string): Promise<RecordSendResult> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 600));
    return {
      ...MOCK_SEND_RESULT,
      counts: { total: sessionIds.length, newSessions: 0, previouslySent: sessionIds.length, notFoundInReports: 0 },
      skippedRowNumbers: [],
    };
  }
  const res = await fetch('/api/dashboard/sent/paste', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ sessionIds, note }),
  });
  return parseSendResponse(res, 'recordSendPaste');
}

/** Opens the stored original file for a send batch (signed URL, like downloadReport). */
export async function downloadSentFile(batchId: string): Promise<void> {
  if (USE_MOCKS) return;
  const res = await fetch(`/api/dashboard/sent/batches/${batchId}/download`, {
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`downloadSentFile failed: ${res.status}`);
  const { url } = (await res.json()) as { url: string };
  if (!url) throw new Error('downloadSentFile: response missing url');
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Undo a mistaken send record; affected rows are restamped server-side. */
export async function deleteSendBatch(batchId: string): Promise<void> {
  if (USE_MOCKS) return;
  const res = await fetch(`/api/dashboard/sent/batches/${batchId}`, {
    method: 'DELETE',
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`deleteSendBatch failed: ${res.status}`);
}

// ============================================================
// Engagement Health
// ============================================================

interface DbSnapshot {
  id: string;
  generated_at: string;
  opportunities_scanned: number | null;
  alerts_count_stalled_start: number | null;
  alerts_count_near_completion: number | null;
  alerts_count_stale_activity: number | null;
  alerts_count_stale_activity_30d: number | null;
  assessment_count: number | null;
  status: 'running' | 'succeeded' | 'failed';
}

interface DbAlert {
  id: string;
  opportunity_id: string;
  opportunity_name: string | null;
  account_name: string | null;
  account_rollup_name?: string | null;
  account_display_name?: string | null;
  owner_id?: string | null;
  owner_name: string | null;
  owner_email?: string | null;
  csm_id?: string | null;
  csm_name?: string | null;
  csm_email?: string | null;
  project_numbers?: string[] | null;
  coachee_name?: string | null;
  sow_start_date: string | null;
  sow_end_date: string | null;
  contracted_hours: number | null;
  logged_hours: number | null;
  pct_complete: number | null;
  last_session_date: string | null;
  days_since_last_session: number | null;
  days_since_start: number | null;
  delivering_affiliates: DeliveringAffiliateRef[] | null;
  alert_type: AlertType;
  created_at: string;
}

interface DbEngagementAssessment {
  opportunity_id: string;
  opportunity_name: string | null;
  account_name: string | null;
  account_rollup_name?: string | null;
  account_display_name?: string | null;
  owner_id?: string | null;
  owner_name: string | null;
  owner_email?: string | null;
  csm_id?: string | null;
  csm_name?: string | null;
  csm_email?: string | null;
  project_numbers?: string[] | null;
  coachee_name: string | null;
  sow_start_date: string | null;
  sow_end_date: string | null;
  contracted_hours: number | null;
  logged_hours: number | null;
  pct_complete: number | null;
  last_session_date: string | null;
  days_since_last_session: number | null;
  days_since_start: number | null;
  days_until_end: number | null;
  engagement_type: string | null;
  engagement_status: string | null;
  delivering_affiliates: DeliveringAffiliateRef[] | null;
  risk_score: number | null;
  risk_tier: EngagementAssessment['riskTier'];
  risk_signals: AlertType[] | null;
  risk_breakdown: EngagementAssessment['riskSignals'] | null;
}

interface DbSnooze {
  opportunity_id: string;
  alert_type: AlertType;
  snoozed_until: string;
  snoozed_by_email: string | null;
}

function mapSnapshot(r: DbSnapshot): EngagementSnapshot {
  return {
    id: r.id,
    generatedAt: r.generated_at,
    opportunitiesScanned: r.opportunities_scanned ?? 0,
    counts: {
      stalledStart: r.alerts_count_stalled_start ?? 0,
      nearCompletion: r.alerts_count_near_completion ?? 0,
      staleActivity: r.alerts_count_stale_activity ?? 0,
      staleActivity30d: r.alerts_count_stale_activity_30d ?? 0,
    },
  };
}

function mapAssessment(a: DbEngagementAssessment): EngagementAssessment {
  const breakdown = Array.isArray(a.risk_breakdown) ? a.risk_breakdown : [];
  return {
    opportunityId: a.opportunity_id,
    opportunityName: a.opportunity_name,
    accountName: a.account_name,
    accountRollupName: a.account_rollup_name ?? null,
    accountDisplayName: a.account_display_name ?? null,
    ownerId: a.owner_id ?? null,
    ownerName: a.owner_name,
    ownerEmail: a.owner_email ?? null,
    csmId: a.csm_id ?? null,
    csmName: a.csm_name ?? null,
    csmEmail: a.csm_email ?? null,
    projectNumbers: Array.isArray(a.project_numbers) ? a.project_numbers : [],
    coacheeName: a.coachee_name,
    sowStartDate: a.sow_start_date,
    sowEndDate: a.sow_end_date,
    contractedHours: a.contracted_hours,
    loggedHours: a.logged_hours ?? 0,
    pctComplete: a.pct_complete,
    lastSessionDate: a.last_session_date,
    daysSinceLastSession: a.days_since_last_session,
    daysSinceStart: a.days_since_start,
    daysUntilEnd: a.days_until_end,
    engagementType: a.engagement_type,
    engagementStatus: a.engagement_status,
    deliveringAffiliates: a.delivering_affiliates ?? [],
    riskScore: a.risk_score,
    riskTier: a.risk_tier,
    riskSignals: breakdown,
  };
}

function mapAlert(
  a: DbAlert,
  snoozesByKey: Map<string, DbSnooze>,
  statusByOpportunity: Map<string, string | null>,
): AlertRow {
  const key = `${a.opportunity_id}::${a.alert_type}`;
  const snooze = snoozesByKey.get(key);
  return {
    id: a.id,
    opportunityId: a.opportunity_id,
    opportunityName: a.opportunity_name,
    accountName: a.account_name,
    accountRollupName: a.account_rollup_name ?? null,
    accountDisplayName: a.account_display_name ?? null,
    ownerId: a.owner_id ?? null,
    ownerName: a.owner_name,
    ownerEmail: a.owner_email ?? null,
    csmId: a.csm_id ?? null,
    csmName: a.csm_name ?? null,
    csmEmail: a.csm_email ?? null,
    projectNumbers: Array.isArray(a.project_numbers) ? a.project_numbers : [],
    coacheeName: a.coachee_name ?? null,
    sowStartDate: a.sow_start_date,
    sowEndDate: a.sow_end_date,
    contractedHours: a.contracted_hours,
    loggedHours: a.logged_hours ?? 0,
    pctComplete: a.pct_complete,
    lastSessionDate: a.last_session_date,
    daysSinceLastSession: a.days_since_last_session,
    daysSinceStart: a.days_since_start,
    engagementStatus: statusByOpportunity.get(a.opportunity_id) ?? null,
    deliveringAffiliates: a.delivering_affiliates ?? [],
    alertType: a.alert_type,
    snooze: snooze
      ? { snoozedUntil: snooze.snoozed_until, snoozedByEmail: snooze.snoozed_by_email }
      : null,
  };
}

function getAllEngagementAssessments(snapshotId: string): Promise<DbEngagementAssessment[]> {
  const supabase = getSupabase();
  return fetchAllPages<DbEngagementAssessment>(
    (from, to) => supabase
      .from('engagement_health_engagements')
      .select('*')
      .eq('snapshot_id', snapshotId)
      .order('opportunity_id', { ascending: true })
      .range(from, to),
    'getEngagementHealth (engagements)',
  );
}

function getAllEngagementAlerts(snapshotId: string): Promise<DbAlert[]> {
  const supabase = getSupabase();
  return fetchAllPages<DbAlert>(
    (from, to) => supabase
      .from('engagement_health_alerts')
      .select('*')
      .eq('snapshot_id', snapshotId)
      .order('id', { ascending: true })
      .range(from, to),
    'getEngagementHealth (alerts)',
  );
}

function getAllActiveEngagementSnoozes(nowIso: string): Promise<DbSnooze[]> {
  const supabase = getSupabase();
  return fetchAllPages<DbSnooze>(
    (from, to) => supabase
      .from('engagement_health_snoozes')
      .select('*')
      .gt('snoozed_until', nowIso)
      .order('opportunity_id', { ascending: true })
      .range(from, to),
    'getEngagementHealth (snoozes)',
  );
}

export async function getEngagementHealth(): Promise<EngagementHealthPayload> {
  if (USE_MOCKS) {
    return {
      snapshot: ENGAGEMENT_SNAPSHOT,
      alerts: ENGAGEMENT_ALERTS,
      engagements: ENGAGEMENT_ASSESSMENTS,
      requiresRefresh: false,
    };
  }

  const supabase = getSupabase();
  const { data: snapshots, error: snapErr } = await supabase
    .from('engagement_health_snapshots')
    .select('*')
    .eq('status', 'succeeded')
    .order('generated_at', { ascending: false })
    .limit(1);

  if (snapErr) throw new Error(`getEngagementHealth (snapshot) failed: ${snapErr.message}`);
  const latest = (snapshots as DbSnapshot[] | null)?.[0];
  if (!latest) return { snapshot: null, alerts: [], engagements: [], requiresRefresh: false };

  const [alertRows, snoozeRows, engagementRows] = await Promise.all([
    getAllEngagementAlerts(latest.id),
    getAllActiveEngagementSnoozes(new Date().toISOString()),
    getAllEngagementAssessments(latest.id),
  ]);

  const snoozesByKey = new Map<string, DbSnooze>();
  for (const s of snoozeRows) {
    snoozesByKey.set(`${s.opportunity_id}::${s.alert_type}`, s);
  }

  const engagements = engagementRows.map(mapAssessment);
  const statusByOpportunity = new Map(
    engagements.map((engagement) => [engagement.opportunityId, engagement.engagementStatus]),
  );
  const alerts = alertRows.map((a) =>
    mapAlert(a, snoozesByKey, statusByOpportunity),
  );
  return {
    snapshot: mapSnapshot(latest),
    alerts,
    engagements,
    requiresRefresh: latest.assessment_count == null,
  };
}

export async function refreshEngagementHealth(): Promise<EngagementHealthPayload> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 800));
    return {
      snapshot: ENGAGEMENT_SNAPSHOT,
      alerts: ENGAGEMENT_ALERTS,
      engagements: ENGAGEMENT_ASSESSMENTS,
      requiresRefresh: false,
    };
  }
  const res = await fetch('/api/dashboard/engagement-health/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
  });
  if (!res.ok) throw new Error(`refreshEngagementHealth failed: ${res.status}`);
  return getEngagementHealth();
}

export async function snoozeAlert(opportunityId: string, alertType: AlertType): Promise<void> {
  if (USE_MOCKS) return;
  const res = await fetch('/api/dashboard/engagement-health/snooze', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ opportunityId, alertType }),
  });
  if (!res.ok) throw new Error(`snoozeAlert failed: ${res.status}`);
}

export async function unsnoozeAlert(opportunityId: string, alertType: AlertType): Promise<void> {
  if (USE_MOCKS) return;
  const res = await fetch(
    `/api/dashboard/engagement-health/snooze/${encodeURIComponent(opportunityId)}/${encodeURIComponent(alertType)}`,
    { method: 'DELETE', headers: await authHeader() },
  );
  if (!res.ok) throw new Error(`unsnoozeAlert failed: ${res.status}`);
}

// ============================================================
// Internal AM/CSM engagement-health digest
// ============================================================

export async function getEngagementDigestConfig(): Promise<EngagementDigestConfig> {
  if (USE_MOCKS) {
    return {
      deliveryMode: 'disabled', liveSendEnabled: false, testSendEnabled: false,
      testRecipient: null, cronEnabled: false, cron: '0 8 1,15 * *',
      timezone: 'America/New_York', nextScheduledRunAt: null, excludedAccountRollups: ['AbbVie'],
      sender: 'Ariel Engagement Health <intel@kelm.ai>',
    };
  }
  const res = await fetch('/api/dashboard/engagement-digest/config', { headers: await authHeader() });
  if (!res.ok) throw new Error(`getEngagementDigestConfig failed: ${res.status}`);
  const json = (await res.json()) as { config: EngagementDigestConfig };
  return json.config;
}

export async function getEngagementDigestRuns(): Promise<EngagementDigestRun[]> {
  if (USE_MOCKS) return [];
  const res = await fetch('/api/dashboard/engagement-digest/runs', { headers: await authHeader() });
  if (!res.ok) throw new Error(`getEngagementDigestRuns failed: ${res.status}`);
  const json = (await res.json()) as { runs: EngagementDigestRun[] };
  return json.runs;
}

export async function getEngagementDigestRun(id: string): Promise<EngagementDigestRunDetail> {
  if (USE_MOCKS) throw new Error('No mock engagement digest run exists');
  const res = await fetch(`/api/dashboard/engagement-digest/runs/${encodeURIComponent(id)}`, {
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`getEngagementDigestRun failed: ${res.status}`);
  return (await res.json()) as EngagementDigestRunDetail;
}

export async function generateEngagementDigestPreview(): Promise<{ runId: string }> {
  if (USE_MOCKS) return { runId: 'mock-digest-run' };
  const res = await fetch('/api/dashboard/engagement-digest/generate', {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
  });
  if (!res.ok) throw new Error(`generateEngagementDigestPreview failed: ${res.status}`);
  return (await res.json()) as { runId: string };
}

export async function sendEngagementDigestRun(
  id: string,
  mode: 'test' | 'live',
  retry = false,
): Promise<{ summary?: { sent: number; skipped: number; failed: number }; runId?: string }> {
  if (USE_MOCKS) return { summary: { sent: 0, skipped: 0, failed: 0 } };
  const endpoint = retry ? 'retry' : mode === 'test' ? 'send-test' : 'send-live';
  const res = await fetch(`/api/dashboard/engagement-digest/runs/${encodeURIComponent(id)}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: retry ? JSON.stringify({ mode }) : undefined,
  });
  if (!res.ok) throw new Error(`sendEngagementDigestRun failed: ${res.status}`);
  return (await res.json()) as { summary?: { sent: number; skipped: number; failed: number }; runId?: string };
}

export async function deleteReport(id: string): Promise<void> {
  if (USE_MOCKS) return;
  const res = await fetch(`/api/dashboard/reports/${id}`, {
    method: 'DELETE',
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`deleteReport failed: ${res.status}`);
}

// ============================================================
// Coach outreach
// ============================================================

interface DbOutreachRun {
  generation_scope?: EngagementScope | null;
  id: string;
  generated_at: string;
  workflow: OutreachWorkflow;
  drafts_count: number | null;
  engagements_count: number | null;
  suppressed_count: number | null;
  status: 'running' | 'succeeded' | 'failed';
}

interface DbOutreachDraft {
  id: string;
  run_id: string;
  workflow: OutreachWorkflow;
  recipient_name: string | null;
  recipient_email: string | null;
  coach_name: string | null;
  coach_email: string | null;
  subject: string;
  body_md: string;
  status: OutreachDraftStatus;
  reviewed_by_email: string | null;
  reviewed_at: string | null;
  reject_reason: string | null;
}

interface DbOutreachEngagement {
  scope_recorded?: boolean;
  engagement_status?: string | null;
  account_display_name?: string | null;
  draft_id: string;
  opportunity_id: string;
  opportunity_name: string | null;
  account_name: string | null;
  coachee_name: string | null;
  project_number: string | null;
  hours_contracted: number | null;
  hours_completed: number | null;
  hours_remaining: number | null;
  sow_start_date: string | null;
  sow_end_date: string | null;
  last_session_date: string | null;
  days_since_last_session: number | null;
  days_since_start: number | null;
  source_alert_type: string | null;
  ledger_note: string | null;
}

function mapOutreachEngagement(e: DbOutreachEngagement): OutreachEngagement {
  return {
    engagementStatus: e.scope_recorded ? e.engagement_status ?? null : undefined,
    accountDisplayName: e.account_display_name ?? null,
    opportunityId: e.opportunity_id,
    opportunityName: e.opportunity_name,
    accountName: e.account_name,
    coacheeName: e.coachee_name,
    projectNumber: e.project_number,
    hoursContracted: e.hours_contracted,
    hoursCompleted: e.hours_completed,
    hoursRemaining: e.hours_remaining,
    sowStartDate: e.sow_start_date,
    sowEndDate: e.sow_end_date,
    lastSessionDate: e.last_session_date,
    daysSinceLastSession: e.days_since_last_session,
    daysSinceStart: e.days_since_start,
    sourceAlertType: e.source_alert_type,
    ledgerNote: e.ledger_note,
  };
}

// Mutable mock state so approve/reject/skip persist across refetches in design mode.
let outreachMockState: OutreachPayload = OUTREACH_MOCK;
// Mutable inbox mock so confirm/relabel verdicts persist across refetches too.
let outreachInboxMockState: InboundReplyRow[] = OUTREACH_INBOX_MOCK;

export async function getOutreachConfig(): Promise<OutreachRuntimeConfig> {
  if (USE_MOCKS) {
    return {
      deliveryMode: 'test',
      testRecipient: 'test1@arielgroup.com',
      liveSendEnabled: false,
      inboundEmailEnabled: true,
      replyParsingEnabled: true,
      replyDomain: 'replies.affiliate-arielgroup.com',
    };
  }
  const res = await fetch('/api/dashboard/outreach/config', { headers: await authHeader() });
  if (!res.ok) throw new Error(`getOutreachConfig failed: ${res.status}`);
  const json = (await res.json()) as { config: OutreachRuntimeConfig };
  return json.config;
}

export async function getOutreachDrafts(workflow?: OutreachWorkflow): Promise<OutreachPayload> {
  if (USE_MOCKS) return outreachMockState;

  const supabase = getSupabase();
  // The monthly cron generates coach_statement and stale_activity_30d as two
  // separate runs in one execution, so "newest run overall" would hide one of
  // them. Scope to the latest run for the selected workflow.
  let runQuery = supabase
    .from('outreach_runs')
    .select('*')
    .eq('status', 'succeeded')
    .order('generated_at', { ascending: false })
    .limit(1);
  if (workflow) runQuery = runQuery.eq('workflow', workflow);
  const { data: runs, error: runErr } = await runQuery;
  if (runErr) throw new Error(`getOutreachDrafts (run) failed: ${runErr.message}`);
  const run = (runs as DbOutreachRun[] | null)?.[0];
  if (!run) return { run: null, drafts: [] };

  const { data: draftData, error: draftErr } = await supabase
    .from('outreach_drafts')
    .select('*')
    .eq('run_id', run.id)
    .order('coach_name');
  if (draftErr) throw new Error(`getOutreachDrafts (drafts) failed: ${draftErr.message}`);
  const drafts = (draftData ?? []) as DbOutreachDraft[];

  const engByDraft = new Map<string, OutreachEngagement[]>();
  if (drafts.length) {
    const { data: engData, error: engErr } = await supabase
      .from('outreach_draft_engagements')
      .select('*')
      .in('draft_id', drafts.map((d) => d.id));
    if (engErr) throw new Error(`getOutreachDrafts (engagements) failed: ${engErr.message}`);
    for (const e of (engData ?? []) as DbOutreachEngagement[]) {
      const arr = engByDraft.get(e.draft_id) ?? [];
      arr.push(mapOutreachEngagement(e));
      engByDraft.set(e.draft_id, arr);
    }
  }

  return {
    run: {
      id: run.id,
      generatedAt: run.generated_at,
      workflow: run.workflow,
      draftsCount: run.drafts_count ?? drafts.length,
      engagementsCount: run.engagements_count ?? 0,
      suppressedCount: run.suppressed_count ?? 0,
      generationScope: run.generation_scope ?? null,
    },
    drafts: drafts.map((d) => ({
      id: d.id,
      runId: d.run_id,
      workflow: d.workflow,
      recipientName: d.recipient_name,
      recipientEmail: d.recipient_email,
      coachName: d.coach_name,
      coachEmail: d.coach_email,
      subject: d.subject,
      bodyMd: d.body_md,
      status: d.status,
      reviewedByEmail: d.reviewed_by_email,
      reviewedAt: d.reviewed_at,
      rejectReason: d.reject_reason,
      engagements: engByDraft.get(d.id) ?? [],
    })),
  };
}

export async function generateOutreach(
  workflow: OutreachWorkflow,
  scope?: EngagementScope,
): Promise<{ runId: string; draftsCount: number; engagementsCount: number }> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 900));
    const drafts = OUTREACH_MOCK.drafts.filter((d) => !scope || draftMatchesScope(d.engagements, scope)).map((d) => ({ ...d, workflow }));
    const engagementsCount = drafts.reduce((n, d) => n + d.engagements.length, 0);
    outreachMockState = { drafts, run: { ...OUTREACH_MOCK.run!, workflow, generationScope: scope, draftsCount: drafts.length, engagementsCount } };
    return { runId: 'mock-run', draftsCount: drafts.length, engagementsCount };
  }
  const res = await fetch('/api/dashboard/outreach/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ workflow, scope }),
  });
  if (!res.ok) throw new Error(`generateOutreach failed: ${res.status}`);
  const json = await res.json();
  return { runId: json.runId, draftsCount: json.draftsCount, engagementsCount: json.engagementsCount };
}

export async function patchOutreachDraft(
  id: string,
  patch: { subject?: string; bodyMd?: string },
): Promise<void> {
  if (USE_MOCKS) {
    outreachMockState = {
      ...outreachMockState,
      drafts: outreachMockState.drafts.map((d) =>
        d.id === id
          ? {
              ...d,
              ...(patch.subject !== undefined ? { subject: patch.subject } : {}),
              ...(patch.bodyMd !== undefined ? { bodyMd: patch.bodyMd } : {}),
              status: 'edited',
            }
          : d,
      ),
    };
    return;
  }
  const res = await fetch(`/api/dashboard/outreach/drafts/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`patchOutreachDraft failed: ${res.status}`);
}

async function outreachAction(
  id: string,
  action: 'approve' | 'reject' | 'skip',
  body?: unknown,
): Promise<void> {
  if (USE_MOCKS) {
    const status: OutreachDraftStatus =
      action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'skipped';
    outreachMockState = {
      ...outreachMockState,
      drafts: outreachMockState.drafts.map((d) => (d.id === id ? { ...d, status } : d)),
    };
    return;
  }
  const res = await fetch(`/api/dashboard/outreach/drafts/${id}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`outreach ${action} failed: ${res.status}`);
}

export async function getOutreachInbox(): Promise<InboundReplyRow[]> {
  if (USE_MOCKS) return outreachInboxMockState;
  const res = await fetch('/api/dashboard/outreach/inbox', { headers: await authHeader() });
  if (!res.ok) throw new Error(`getOutreachInbox failed: ${res.status}`);
  const json = (await res.json()) as { replies?: InboundReplyRow[] };
  return (json.replies ?? []).map((r) => ({
    ...r,
    ledger: r.ledger ?? [],
    review: r.review ?? null,
  }));
}

export interface ReplyReviewPatch {
  queueStatus?: ReplyQueueStatus;
  note?: string | null;
  followUpDueAt?: string | null;
  labelOverrides?: ReplyLabelOverride[];
}

/** Move a reply through the triage queue without bypassing test/live isolation. */
export async function patchReplyReview(
  replyId: string,
  patch: ReplyReviewPatch,
): Promise<ReplyReview> {
  if (USE_MOCKS) {
    let updated: ReplyReview | null = null;
    outreachInboxMockState = outreachInboxMockState.map((reply) => {
      if (reply.id !== replyId) return reply;
      const now = new Date().toISOString();
      const queueStatus = patch.queueStatus ?? reply.review?.queue_status ?? 'new';
      const isFollowUp = queueStatus === 'follow_up';
      updated = {
        reply_id: reply.id,
        queue_status: queueStatus,
        note: patch.note !== undefined ? patch.note : reply.review?.note ?? null,
        follow_up_owner: isFollowUp ? reply.review?.follow_up_owner ?? 'mock-user' : null,
        follow_up_owner_email: isFollowUp ? reply.review?.follow_up_owner_email ?? 'you@arielgroup.com' : null,
        follow_up_due_at: isFollowUp
          ? patch.followUpDueAt !== undefined
            ? patch.followUpDueAt
            : reply.review?.follow_up_due_at ?? null
          : null,
        label_overrides: patch.labelOverrides ?? reply.review?.label_overrides ?? [],
        reviewed_by_email: 'you@arielgroup.com',
        reviewed_at: now,
        resolved_at: queueStatus === 'acknowledged' || queueStatus === 'dismissed' ? now : null,
        created_at: reply.review?.created_at ?? now,
        updated_at: now,
      };
      const ledger = reply.delivery_mode === 'live'
        ? reply.ledger.map((entry) => {
            if (queueStatus === 'dismissed') return entry;
            if (queueStatus === 'acknowledged' && entry.review_status === 'unverified') {
              return {
                ...entry,
                review_status: 'confirmed' as const,
                reviewed_by_email: 'you@arielgroup.com',
                reviewed_at: now,
              };
            }
            return entry;
          })
        : reply.ledger;
      return { ...reply, review: updated, ledger };
    });
    if (!updated) throw new Error('Reply not found');
    return updated;
  }

  const res = await fetch(`/api/dashboard/outreach/replies/${replyId}/review`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`patchReplyReview failed: ${res.status}`);
  const json = (await res.json()) as { review: ReplyReview };
  return json.review;
}

/** Verify one parsed engagement: 'confirmed' keeps the AI tags; 'edited' swaps them. */
export async function patchLedgerEntry(
  id: string,
  patch: { reviewStatus: 'confirmed' | 'edited'; categories?: string[] },
): Promise<void> {
  if (USE_MOCKS) {
    outreachInboxMockState = outreachInboxMockState.map((r) => ({
      ...r,
      ledger: r.ledger.map((e) =>
        e.id === id
          ? {
              ...e,
              review_status: patch.reviewStatus,
              categories: patch.categories ?? e.categories,
              category: patch.categories?.[0] ?? e.category,
              reviewed_by_email: 'you@arielgroup.com',
              reviewed_at: new Date().toISOString(),
            }
          : e,
      ),
    }));
    return;
  }
  const res = await fetch(`/api/dashboard/outreach/ledger/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`patchLedgerEntry failed: ${res.status}`);
}

/** Confirm every still-unverified matched engagement on a reply in one call. */
export async function confirmAllReplyLedger(replyId: string): Promise<number> {
  if (USE_MOCKS) {
    let confirmed = 0;
    outreachInboxMockState = outreachInboxMockState.map((r) =>
      r.id !== replyId
        ? r
        : {
            ...r,
            ledger: r.ledger.map((e) => {
              if (e.review_status !== 'unverified') return e;
              confirmed += 1;
              return { ...e, review_status: 'confirmed', reviewed_by_email: 'you@arielgroup.com', reviewed_at: new Date().toISOString() };
            }),
          },
    );
    return confirmed;
  }
  const res = await fetch(`/api/dashboard/outreach/replies/${replyId}/confirm-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
  });
  if (!res.ok) throw new Error(`confirmAllReplyLedger failed: ${res.status}`);
  const json = (await res.json()) as { confirmed?: number };
  return json.confirmed ?? 0;
}

/** The accumulated, verified context for one coach — feeds the review drawer. */
export async function getCoachLedger(email: string): Promise<LedgerEntry[]> {
  if (USE_MOCKS) {
    const target = email.toLowerCase();
    return outreachInboxMockState
      .filter((r) => r.from_email?.toLowerCase() === target)
      .flatMap((r) => r.ledger);
  }
  const res = await fetch(`/api/dashboard/outreach/coaches/${encodeURIComponent(email)}/ledger`, {
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`getCoachLedger failed: ${res.status}`);
  const json = (await res.json()) as { entries?: LedgerEntry[] };
  return json.entries ?? [];
}

export const approveOutreachDraft = (id: string): Promise<void> => outreachAction(id, 'approve');
export const rejectOutreachDraft = (id: string, reason?: string): Promise<void> =>
  outreachAction(id, 'reject', reason ? { reason } : undefined);
export const skipOutreachDraft = (id: string): Promise<void> => outreachAction(id, 'skip');

export async function suppressOutreachDraft(id: string, days?: number): Promise<void> {
  if (USE_MOCKS) {
    outreachMockState = {
      ...outreachMockState,
      drafts: outreachMockState.drafts.map((d) => (d.id === id ? { ...d, status: 'skipped' } : d)),
    };
    return;
  }
  const res = await fetch(`/api/dashboard/outreach/drafts/${id}/suppress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ days, reason: 'manual' }),
  });
  if (!res.ok) throw new Error(`suppressOutreachDraft failed: ${res.status}`);
}

export interface OutreachSendResult {
  draftId: string;
  outcome: 'sent' | 'dry_run' | 'skipped' | 'failed';
  detail?: string;
  to?: string | null;
  intendedTo?: string | null;
  deliveryMode?: 'test' | 'live';
  resendMessageId?: string | null;
}

export async function sendOutreachDraft(
  id: string,
  dryRun?: boolean,
): Promise<{ sendEnabled: boolean; result: OutreachSendResult }> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 600));
    // Mocks never transmit: report a dry-run with sending disabled.
    return { sendEnabled: false, result: { draftId: id, outcome: 'dry_run', to: 'coach@example.com' } };
  }
  const res = await fetch(`/api/dashboard/outreach/drafts/${id}/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ dryRun }),
  });
  if (!res.ok) throw new Error(`sendOutreachDraft failed: ${res.status}`);
  return (await res.json()) as { sendEnabled: boolean; result: OutreachSendResult };
}

export async function sendTestOutreachDraft(
  id: string,
): Promise<{ result: OutreachSendResult }> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 600));
    const draft = outreachMockState.drafts.find((d) => d.id === id);
    return {
      result: {
        draftId: id,
        outcome: draft?.status === 'approved' ? 'sent' : 'skipped',
        to: 'test1@arielgroup.com',
        intendedTo: draft?.recipientEmail ?? null,
        deliveryMode: 'test',
        resendMessageId: draft?.status === 'approved' ? 'test-resend-id' : null,
      },
    };
  }
  const res = await fetch(`/api/dashboard/outreach/drafts/${id}/test-send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
  });
  if (!res.ok) throw new Error(`sendTestOutreachDraft failed: ${res.status}`);
  return (await res.json()) as { result: OutreachSendResult };
}

export async function sendApprovedOutreach(
  workflow?: OutreachWorkflow,
  dryRun?: boolean,
  draftIds?: string[],
): Promise<{
  sendEnabled: boolean;
  deliveryMode: OutreachDeliveryMode;
  testRecipient: string | null;
  summary: Record<string, number>;
  results: OutreachSendResult[];
}> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 800));
    const approved = outreachMockState.drafts.filter((d) => d.status === 'approved' && (!draftIds || draftIds.includes(d.id)));
    return {
      sendEnabled: false,
      deliveryMode: 'test',
      testRecipient: 'test1@arielgroup.com',
      summary: { sent: approved.length, failed: 0, skipped: 0, dry_run: 0 },
      results: approved.map((d) => ({
        draftId: d.id,
        outcome: 'sent' as const,
        to: 'test1@arielgroup.com',
        intendedTo: d.recipientEmail,
        deliveryMode: 'test' as const,
      })),
    };
  }
  const res = await fetch('/api/dashboard/outreach/send-approved', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ dryRun, workflow, draftIds }),
  });
  if (!res.ok) throw new Error(`sendApprovedOutreach failed: ${res.status}`);
  return (await res.json()) as {
    sendEnabled: boolean;
    deliveryMode: OutreachDeliveryMode;
    testRecipient: string | null;
    summary: Record<string, number>;
    results: OutreachSendResult[];
  };
}

// ============================================================
// SOW generator
// ============================================================

interface DbSowDocument {
  id: string;
  opportunity_sf_id: string;
  opportunity_number: string | null;
  opportunity_name: string | null;
  account_name: string | null;
  template_type: SowTemplateType;
  version: number;
  file_name: string | null;
  generated_by_email: string | null;
  created_at: string;
  field_values: SowFieldValues;
}

function mapDbSowDocument(d: DbSowDocument): SowDocumentRow {
  return {
    id: d.id,
    opportunitySfId: d.opportunity_sf_id,
    opportunityNumber: d.opportunity_number,
    opportunityName: d.opportunity_name,
    accountName: d.account_name,
    templateType: d.template_type,
    version: d.version,
    fileName: d.file_name,
    generatedByEmail: d.generated_by_email,
    createdAt: d.created_at,
    fieldValues: d.field_values,
  };
}

export async function searchSowOpportunities(q: string): Promise<SowOpportunityHit[]> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 250));
    const needle = q.trim().toLowerCase();
    return SOW_OPPORTUNITIES.filter(
      (o) =>
        (o.number ?? '').toLowerCase().includes(needle) ||
        (o.name ?? '').toLowerCase().includes(needle) ||
        (o.accountName ?? '').toLowerCase().includes(needle),
    );
  }
  const res = await fetch(`/api/dashboard/sow/opportunities?q=${encodeURIComponent(q)}`, {
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`searchSowOpportunities failed: ${res.status}`);
  const json = (await res.json()) as { results: SowOpportunityHit[] };
  return json.results;
}

export async function getSowPrefill(
  idOrNumber: string,
  template?: SowTemplateType,
): Promise<SowPrefill> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 500));
    return mockSowPrefill(idOrNumber, template);
  }
  const qs = template ? `?template=${template}` : '';
  const res = await fetch(
    `/api/dashboard/sow/opportunities/${encodeURIComponent(idOrNumber)}/prefill${qs}`,
    { headers: await authHeader() },
  );
  if (res.status === 404) throw new Error('Opportunity not found');
  if (!res.ok) throw new Error(`getSowPrefill failed: ${res.status}`);
  return (await res.json()) as SowPrefill;
}

export interface GenerateSowResponse {
  id: string | null;
  version: number | null;
  fileName: string;
  downloadUrl: string | null;
}

export async function generateSow(
  opportunitySfId: string,
  fields: SowFieldValues,
): Promise<GenerateSowResponse> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 1200));
    const meta = SOW_OPPORTUNITIES.find((o) => o.id === opportunitySfId);
    const label = fields.templateType === 'coaching_sow' ? 'Ariel Coach SOW' : 'Ariel SOW';
    const doc = mockAppendSowDocument({
      opportunitySfId,
      opportunityNumber: meta?.number ?? null,
      opportunityName: meta?.name ?? null,
      accountName: meta?.accountName ?? null,
      templateType: fields.templateType,
      fileName: `${label} - ${meta?.accountName ?? 'Account'} - ${meta?.number ?? ''}.docx`,
      generatedByEmail: 'you@arielgroup.com',
      fieldValues: fields,
    });
    return { id: doc.id, version: doc.version, fileName: doc.fileName ?? 'SOW.docx', downloadUrl: null };
  }
  const res = await fetch('/api/dashboard/sow/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ opportunitySfId, fields }),
  });
  if (res.status === 403) throw new Error('You need the Editor or Project Manager role to create SOWs. Ask an admin.');
  if (!res.ok) throw await readSowError(res);
  return (await res.json()) as GenerateSowResponse;
}

export async function listSowDocuments(opportunitySfId?: string): Promise<SowDocumentRow[]> {
  if (USE_MOCKS) {
    return opportunitySfId
      ? SOW_DOCUMENTS.filter((d) => d.opportunitySfId === opportunitySfId)
      : [...SOW_DOCUMENTS];
  }
  const supabase = getSupabase();
  let query = supabase
    .from('sow_documents')
    .select('id, opportunity_sf_id, opportunity_number, opportunity_name, account_name, template_type, version, file_name, generated_by_email, created_at, field_values')
    .order('created_at', { ascending: false })
    .limit(100);
  if (opportunitySfId) query = query.eq('opportunity_sf_id', opportunitySfId);
  const { data, error } = await query;
  if (error) throw new Error(`listSowDocuments failed: ${error.message}`);
  return (data as DbSowDocument[]).map(mapDbSowDocument);
}

export async function downloadSowDocument(id: string): Promise<void> {
  if (USE_MOCKS) return; // no Storage in mocks
  const res = await fetch(`/api/dashboard/sow/documents/${id}/download`, {
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`downloadSowDocument failed: ${res.status}`);
  const { url } = (await res.json()) as { url: string };
  if (!url) throw new Error('downloadSowDocument: response missing url');
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function deleteSowDocument(id: string): Promise<void> {
  if (USE_MOCKS) {
    const i = SOW_DOCUMENTS.findIndex((d) => d.id === id);
    if (i >= 0) SOW_DOCUMENTS.splice(i, 1);
    return;
  }
  const res = await fetch(`/api/dashboard/sow/documents/${id}`, {
    method: 'DELETE',
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`deleteSowDocument failed: ${res.status}`);
}

// ---------------------------------------------------------------------------
// Chemistry checks
// ---------------------------------------------------------------------------

interface DbChemCase {
  id: string; tag: string; client_name: string; client_email: string | null;
  client_timezone: string; assistant_name: string | null; assistant_email: string | null;
  pm_name: string; pm_email: string | null; state: ChemistryCase['state']; paused: boolean;
  target_date: string | null; waiting_on: string | null; next_action: string | null;
  next_action_at: string | null; escalation_reason: string | null; selected_coach: string | null;
  created_at: string;
}
interface DbChemTrack {
  id: string; case_id: string; coach_name: string; coach_email: string | null;
  coach_timezone: string | null; state: ChemistryCase['tracks'][number]['state'];
  waiting_on: string | null; next_action: string | null; next_action_at: string | null;
  proposed_start: string | null; proposed_end: string | null; proposed_timezone: string | null;
  scheduled_start: string | null; scheduled_end: string | null;
}
interface DbChemWindow {
  id: string; case_id: string; track_id: string | null; party: 'client' | 'coach';
  source_text: string; start_at: string; end_at: string; timezone: string;
  status: 'valid' | 'consumed' | 'expired';
}
interface DbChemAction {
  case_id: string; at: string; actor: ChemistryTimelineEvent['actor'];
  kind: string; title: string; detail: string | null; shadow: boolean;
}

const CHEM_KIND_FALLBACK: Record<string, ChemistryTimelineEvent['kind']> = { state_change: 'note' };

/** Live mode reads the chemistry_check_* tables via RLS; mock mode serves the
 * design fixtures. DB-backed cases get live=true so the drawer offers the
 * server-backed reply flow. */
export async function listChemistryCases(): Promise<ChemistryCase[]> {
  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 250));
    return CHEMISTRY_CASES_MOCK;
  }
  const sb = getSupabase();
  const [cases, tracks, windows, actions] = await Promise.all([
    sb.from('chemistry_check_cases').select('*').neq('state', 'closed').order('created_at', { ascending: false }).limit(200),
    sb.from('chemistry_check_tracks').select('*').order('created_at', { ascending: true }),
    sb.from('chemistry_check_availability').select('*'),
    sb.from('chemistry_check_actions').select('*').order('at', { ascending: true }),
  ]);
  for (const r of [cases, tracks, windows, actions]) {
    if (r.error) throw new Error(`listChemistryCases failed: ${r.error.message}`);
  }
  const trackRows = (tracks.data ?? []) as DbChemTrack[];
  const windowRows = (windows.data ?? []) as DbChemWindow[];
  const actionRows = (actions.data ?? []) as DbChemAction[];

  return ((cases.data ?? []) as DbChemCase[]).map((c) => {
    const caseWindows = windowRows.filter((w) => w.case_id === c.id);
    const clientWindows = caseWindows.filter((w) => w.track_id === null);
    const mapWindow = (w: DbChemWindow) => ({
      party: w.party,
      sourceText: w.source_text,
      startIso: w.start_at,
      endIso: w.end_at,
      timezone: w.timezone,
      status: w.status,
    });
    return {
      id: c.id,
      tag: c.tag,
      clientName: c.client_name,
      clientEmail: c.client_email ?? '(unknown)',
      clientTimezone: c.client_timezone,
      assistantName: c.assistant_name,
      assistantEmail: c.assistant_email,
      pmName: c.pm_name,
      pmEmail: c.pm_email ?? '—',
      state: c.state,
      targetDate: c.target_date ?? c.created_at,
      createdAt: c.created_at,
      waitingOn: c.paused ? 'Paused' : c.waiting_on ?? '—',
      nextAction: c.next_action ?? '—',
      nextActionAt: c.next_action_at ?? c.created_at,
      escalationReason: c.escalation_reason,
      tracks: trackRows
        .filter((t) => t.case_id === c.id)
        .map((t) => ({
          id: t.id,
          coachName: t.coach_name,
          coachEmail: t.coach_email ?? '(email pending)',
          coachTimezone: t.coach_timezone,
          state: t.state,
          waitingOn: t.waiting_on ?? '—',
          nextAction: t.next_action ?? '—',
          nextActionAt: t.next_action_at ?? c.created_at,
          proposedStart: t.proposed_start,
          proposedEnd: t.proposed_end,
          scheduledStart: t.scheduled_start,
          scheduledEnd: t.scheduled_end,
          availability: [
            ...clientWindows.map(mapWindow),
            ...caseWindows.filter((w) => w.track_id === t.id).map(mapWindow),
          ],
        })),
      timeline: actionRows
        .filter((a) => a.case_id === c.id)
        .map((a) => ({
          at: a.at,
          actor: a.actor,
          kind: (CHEM_KIND_FALLBACK[a.kind] ?? a.kind) as ChemistryTimelineEvent['kind'],
          title: a.title,
          detail: a.detail ?? undefined,
          shadow: a.shadow || undefined,
        })),
      live: true,
    };
  });
}

/** Read the chemistry-check delivery mode before an action can contact people. */
export async function getChemistryRuntime(): Promise<ChemistryRuntime> {
  if (USE_MOCKS) {
    return { sendEnabled: false, meetingMinutes: 45 };
  }
  const res = await fetch('/api/dashboard/chemistry-checks/runtime', {
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`getChemistryRuntime failed: ${res.status}`);
  const json = await res.json();
  return json.runtime as ChemistryRuntime;
}

/** Create a persisted case from validated dashboard fields. Returns the new case id. */
export async function createChemistryCase(input: ChemistryCaseCreateInput): Promise<string> {
  const res = await fetch('/api/dashboard/chemistry-checks/cases', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `createChemistryCase failed: ${res.status}`);
  }
  const json = await res.json();
  return json.case.id as string;
}

async function chemPost(path: string, body?: object): Promise<void> {
  const res = await fetch(`/api/dashboard/chemistry-checks${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) throw new Error(`${path} failed: ${res.status}`);
}

/** PM confirms the Outlook invite exists → track becomes scheduled. */
export async function confirmChemistryTrack(trackId: string): Promise<void> {
  return chemPost(`/tracks/${trackId}/confirm-scheduled`);
}

/** Record a passed meeting's outcome; no_show reopens the track. */
export async function recordChemistryCompletion(trackId: string, outcome: 'complete' | 'no_show'): Promise<void> {
  return chemPost(`/tracks/${trackId}/completion`, { outcome });
}

/** Record the client's coach selection — closes the case. */
export async function recordChemistrySelection(caseId: string, coach: string): Promise<void> {
  return chemPost(`/cases/${caseId}/selection`, { coach });
}

export async function pauseChemistryCase(caseId: string, paused: boolean): Promise<void> {
  return chemPost(`/cases/${caseId}/pause`, { paused });
}

export async function resolveChemistryEscalation(caseId: string): Promise<void> {
  return chemPost(`/cases/${caseId}/resolve-escalation`);
}

/** Feed a participant reply into a persisted case (server parses + applies). */
export async function advanceChemistryCaseLive(
  caseId: string,
  payload: { from: 'client' | 'coach'; coachName?: string | null; text: string },
): Promise<void> {
  const res = await fetch(`/api/dashboard/chemistry-checks/cases/${caseId}/replies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify({ from: payload.from, coachName: payload.coachName ?? undefined, text: payload.text }),
  });
  if (!res.ok) throw new Error(`advanceChemistryCase failed: ${res.status}`);
}

/**
 * Live parse for the Simulate panel. In mock mode returns the canned example;
 * in live mode POSTs to the demo endpoint and falls back to the canned example
 * (marked degraded) if the call fails — the demo must survive a dead network.
 */
export async function simulateChemistryParse(
  req: ChemistrySimulateRequest,
): Promise<ChemistrySimulateResponse> {
  const canned: ChemistrySimulateResponse =
    req.mode === 'kickoff'
      ? { mode: 'kickoff', kickoff: CANNED_KICKOFF_PARSE, degraded: true, model: null }
      : { mode: 'availability', availability: CANNED_AVAILABILITY_PARSE, degraded: true, model: null };

  if (USE_MOCKS) {
    await new Promise((r) => setTimeout(r, 900));
    return { ...canned, degraded: false, model: 'canned-example' };
  }

  try {
    const res = await fetch('/api/dashboard/chemistry-checks/demo/parse', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
      body: JSON.stringify(req),
    });
    if (!res.ok) throw new Error(`simulateChemistryParse failed: ${res.status}`);
    const json = await res.json();
    return {
      mode: req.mode,
      kickoff: json.kickoff ?? undefined,
      availability: json.availability ?? undefined,
      degraded: false,
      model: json.model ?? null,
    };
  } catch {
    return canned;
  }
}

// ============================================================
// Settings › Team (admin only)
// ============================================================

/**
 * Forgot password. Public (no auth header): the person is signed out. The
 * server answers the same way whether or not the address has an account.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  if (USE_MOCKS) return;
  const res = await fetch('/api/auth/password-reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(json?.error?.message ?? `Could not send the reset email (${res.status})`);
  }
}

async function teamRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/dashboard/team${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(await authHeader()), ...(init.headers ?? {}) },
  });
  const json = (await res.json().catch(() => null)) as
    | ({ success: boolean; error?: { message?: string } } & Record<string, unknown>)
    | null;
  if (!res.ok || !json?.success) {
    throw new Error(json?.error?.message ?? `Team request failed (${res.status})`);
  }
  return json as unknown as T;
}

export async function getTeam(): Promise<TeamPayload> {
  if (USE_MOCKS) return mockGetTeam();
  const json = await teamRequest<TeamPayload>('');
  return { members: json.members, activity: json.activity, orgDomains: json.orgDomains };
}

export async function inviteTeamMembers(input: {
  emails: string[];
  role: DashboardRole;
  note?: string;
}): Promise<TeamInviteResult[]> {
  if (USE_MOCKS) return mockInvite(input);
  const json = await teamRequest<{ results: TeamInviteResult[] }>('/invites', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return json.results;
}

export async function resendTeamInvite(userId: string): Promise<void> {
  if (USE_MOCKS) return mockResend(userId);
  await teamRequest(`/members/${encodeURIComponent(userId)}/resend-invite`, { method: 'POST' });
}

export async function changeTeamRole(userId: string, role: DashboardRole): Promise<void> {
  if (USE_MOCKS) return mockChangeRole(userId, role);
  await teamRequest(`/members/${encodeURIComponent(userId)}`, { method: 'PATCH', body: JSON.stringify({ role }) });
}

export async function deactivateTeamMember(userId: string): Promise<void> {
  if (USE_MOCKS) return mockDeactivate(userId);
  await teamRequest(`/members/${encodeURIComponent(userId)}/deactivate`, { method: 'POST' });
}

export async function restoreTeamMember(userId: string): Promise<void> {
  if (USE_MOCKS) return mockRestore(userId);
  await teamRequest(`/members/${encodeURIComponent(userId)}/restore`, { method: 'POST' });
}

export async function revokeTeamInvite(userId: string): Promise<void> {
  if (USE_MOCKS) return mockRevoke(userId);
  await teamRequest(`/members/${encodeURIComponent(userId)}/invite`, { method: 'DELETE' });
}
